import type { I18n, MessageDescriptor } from '@lingui/core';

import { isEffect, type ScreenManifest } from './types';

// Guia de tela (U46, D2/D3): escrito para o usuário, em pt-BR com `msg`, traduzido como a interface.
// Nada técnico: sem endpoint, sem nome de campo do backend, sem regra interna de permissão. Não sai do
// SPEC.md, que é interno. Chega sob demanda (`load<Mod>Guides` na `route.ts`), fora do JS inicial.

export interface ScreenGuideSection {
  readonly title: MessageDescriptor;
  readonly shows: MessageDescriptor;
}

export interface ScreenGuideField {
  readonly label: MessageDescriptor;
  readonly meaning: MessageDescriptor;
}

export interface ScreenGuideStatus {
  readonly value: string;
  readonly label: MessageDescriptor;
  readonly meaning: MessageDescriptor;
}

export interface ScreenGuideAction {
  /** Id da ação no manifesto da tela. */
  readonly id: string;
  readonly whenToUse: MessageDescriptor;
  readonly whatHappens: MessageDescriptor;
  /** O que precisa estar pronto antes (seleção, filtro, registro aberto). */
  readonly requires?: MessageDescriptor;
}

export interface ScreenGuide {
  readonly screenId: string;
  readonly purpose: MessageDescriptor;
  readonly whenToUse: MessageDescriptor;
  readonly sections: readonly ScreenGuideSection[];
  /** Só o que o usuário vê na tela. */
  readonly fields: readonly ScreenGuideField[];
  /** Quando a tela tem workflow. */
  readonly statuses?: readonly ScreenGuideStatus[];
  readonly actions: readonly ScreenGuideAction[];
  /** Ids de telas relacionadas. */
  readonly related?: readonly string[];
  readonly tips?: readonly MessageDescriptor[];
}

/** O que o `<mod>.guide.ts` exporta: os guias do módulo e o carregador do catálogo dele. */
export interface ScreenGuideModule {
  readonly screenGuides: readonly ScreenGuide[];
  readonly loadMessages: () => Promise<void>;
}

/** A porta `load<Mod>Guides` da `route.ts`: `() => import('./<mod>.guide')`. */
export type ScreenGuideLoader = () => Promise<ScreenGuideModule>;

/** Guia de uma tela (U46). Em dev, lança erro para guia sem propósito, sem seção ou com ação repetida. */
export function defineScreenGuide(guide: ScreenGuide): ScreenGuide {
  if (import.meta.env.DEV) {
    const issues: string[] = [];
    if (guide.sections.length === 0) issues.push('guia sem seções');
    const ids = guide.actions.map((a) => a.id);
    for (const id of new Set(ids)) if (ids.indexOf(id) !== ids.lastIndexOf(id)) issues.push(`ação repetida no guia: ${id}`);
    if (issues.length > 0) throw new Error(`guia ${guide.screenId}: ${issues.join('; ')}`);
  }
  return guide;
}

/** Guia × manifesto: mesma tela, toda ação citada existe e toda ação de efeito aparece no guia. */
export function validateGuide(guide: ScreenGuide, manifest: ScreenManifest): string[] {
  const issues: string[] = [];
  if (guide.screenId !== manifest.id) issues.push(`guia de ${guide.screenId} ligado à tela ${manifest.id}`);
  const declared = new Set(manifest.actions.map((a) => a.id));
  const cited = new Set(guide.actions.map((a) => a.id));
  for (const id of cited) if (!declared.has(id)) issues.push(`guia cita a ação ${id}, que não está no manifesto`);
  for (const a of manifest.actions) if (isEffect(a) && !cited.has(a.id)) issues.push(`ação de efeito ${a.id} fora do guia`);
  if (guide.related?.includes(guide.screenId)) issues.push('guia relaciona a própria tela');
  return issues;
}

export interface ResolvedScreenGuide {
  readonly purpose: string;
  readonly whenToUse: string;
  readonly sections: readonly { readonly title: string; readonly shows: string }[];
  readonly fields: readonly { readonly label: string; readonly meaning: string }[];
  readonly statuses: readonly { readonly value: string; readonly label: string; readonly meaning: string }[];
  readonly actions: readonly { readonly id: string; readonly whenToUse: string; readonly whatHappens: string; readonly requires?: string }[];
  readonly tips: readonly string[];
}

/** O guia no idioma ativo. `related` fica com quem resolve, porque depende da visibilidade. */
export function resolveScreenGuide(i18n: I18n, guide: ScreenGuide): ResolvedScreenGuide {
  const _ = (text: MessageDescriptor) => i18n._(text);
  return {
    purpose: _(guide.purpose),
    whenToUse: _(guide.whenToUse),
    sections: guide.sections.map((s) => ({ title: _(s.title), shows: _(s.shows) })),
    fields: guide.fields.map((f) => ({ label: _(f.label), meaning: _(f.meaning) })),
    statuses: (guide.statuses ?? []).map((s) => ({ value: s.value, label: _(s.label), meaning: _(s.meaning) })),
    actions: guide.actions.map((a) => ({
      id: a.id,
      whenToUse: _(a.whenToUse),
      whatHappens: _(a.whatHappens),
      ...(a.requires ? { requires: _(a.requires) } : {}),
    })),
    tips: (guide.tips ?? []).map(_),
  };
}
